// apps/web/src/features/admin/transform-plugins/expression-lang.ts
//
// Pure helpers for the DSL expression editor: autocomplete suggestion logic
// and cursor-context detection. Separated from the CodeMirror integration so
// they are unit-testable without JSDOM or a running editor.

import { BUILTIN_DSL_FUNCTIONS, HOST_FUNCTIONS } from '@capture/shared';
import {
  autocompletion,
  type CompletionContext,
  type CompletionResult,
} from '@codemirror/autocomplete';
import type { Extension } from '@codemirror/state';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// These identifiers are handled as evaluator specials — they appear in DSL
// source but are NOT autocompleted as standalone functions.
const SPECIALS = new Set(['value', 'field', 'config', 'if', 'concat']);

// ---------------------------------------------------------------------------
// Pure types
// ---------------------------------------------------------------------------

export interface AutocompleteSuggestion {
  label: string;
  type: 'function' | 'field' | 'config-key' | 'host-function';
  detail?: string;
}

export interface AutocompleteInput {
  cursorContext: 'top' | 'field-arg' | 'config-arg';
  prefix: string;
  capabilities: string[];
  tenantFieldNames: string[];
  formSchemaKeys: string[];
}

// ---------------------------------------------------------------------------
// Pure: computeAutocompleteSuggestions
// ---------------------------------------------------------------------------

/**
 * Given the cursor context and a prefix string, return autocomplete
 * suggestions. This is a pure function — no CodeMirror dependency.
 */
export function computeAutocompleteSuggestions(
  input: AutocompleteInput,
): AutocompleteSuggestion[] {
  const { cursorContext, prefix, capabilities, tenantFieldNames, formSchemaKeys } = input;
  const lower = prefix.toLowerCase();

  if (cursorContext === 'field-arg') {
    return tenantFieldNames
      .filter((n) => n.toLowerCase().startsWith(lower))
      .map((n) => ({ label: n, type: 'field' as const }));
  }

  if (cursorContext === 'config-arg') {
    return formSchemaKeys
      .filter((k) => k.toLowerCase().startsWith(lower))
      .map((k) => ({ label: k, type: 'config-key' as const }));
  }

  // cursorContext === 'top'
  const builtins: AutocompleteSuggestion[] = Object.keys(BUILTIN_DSL_FUNCTIONS)
    .filter((name) => !SPECIALS.has(name))
    .filter((name) => name.toLowerCase().startsWith(lower))
    .map((name) => ({ label: name, type: 'function' as const, detail: 'built-in' }));

  // Host functions: include only when at least one skarn.read.* capability
  // is declared. We cannot know which product will be targeted until the user
  // types args, so the presence of any read capability is the gate.
  const hasAnyRead = capabilities.some((c) => c.startsWith('skarn.read.'));
  const hostFns: AutocompleteSuggestion[] = hasAnyRead
    ? Object.keys(HOST_FUNCTIONS)
        .filter((name) => name.toLowerCase().startsWith(lower))
        .map((name) => ({
          label: name,
          type: 'host-function' as const,
          detail: `host (caps: ${capabilities.join(', ')})`,
        }))
    : [];

  return [...builtins, ...hostFns];
}

// ---------------------------------------------------------------------------
// Pure: detectCursorContext
// ---------------------------------------------------------------------------

/**
 * Walk backwards from `cursorPos` to find the innermost unclosed function
 * call. Returns 'field-arg' if we're inside `field(…)`, 'config-arg' if
 * inside `config(…)`, and 'top' otherwise.
 */
export function detectCursorContext(
  doc: string,
  cursorPos: number,
): 'top' | 'field-arg' | 'config-arg' {
  let depth = 0;
  for (let i = cursorPos - 1; i >= 0; i--) {
    const ch = doc[i];
    if (ch === ')') {
      depth++;
    } else if (ch === '(') {
      if (depth === 0) {
        // Walk backwards to capture the identifier before the open paren.
        let j = i - 1;
        while (j >= 0 && /[\w.]/.test(doc[j]!)) j--;
        const name = doc.slice(j + 1, i);
        if (name === 'field') return 'field-arg';
        if (name === 'config') return 'config-arg';
        return 'top';
      }
      depth--;
    }
  }
  return 'top';
}

// ---------------------------------------------------------------------------
// CodeMirror integration
// ---------------------------------------------------------------------------

export interface ExpressionLangOptions {
  capabilities: string[];
  tenantFieldNames: string[];
  formSchemaKeys: string[];
}

export function expressionAutocomplete(opts: ExpressionLangOptions): Extension {
  return autocompletion({
    override: [
      (ctx: CompletionContext): CompletionResult | null => {
        // Match identifiers and dotted names (for skarn.lookup, skarn.search)
        const word = ctx.matchBefore(/[\w.]*/);
        if (!word || (word.from === word.to && !ctx.explicit)) return null;

        const doc = ctx.state.doc.toString();
        const cursorContext = detectCursorContext(doc, ctx.pos);
        const suggestions = computeAutocompleteSuggestions({
          ...opts,
          cursorContext,
          prefix: word.text,
        });

        return {
          from: word.from,
          options: suggestions.map((s) => ({
            label: s.label,
            type:
              s.type === 'function'
                ? 'function'
                : s.type === 'host-function'
                  ? 'method'
                  : 'variable',
            detail: s.detail,
          })),
        };
      },
    ],
  });
}

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------

export const EXPRESSION_EDITOR_CSS = `
.cm-editor {
  border: 1px solid var(--color-border, #2a2a2a);
  border-radius: 4px;
  font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
  font-size: var(--text-sm, 13px);
  min-height: 80px;
}
.cm-editor.cm-focused {
  outline: 2px solid var(--color-primary, #ea580c);
  outline-offset: -1px;
}
.cm-content {
  padding: 8px;
}
`.trim();
